import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  fetchSummary,
  fetchOrdersByBranch,
  fetchOrdersByChannel,
  fetchHourlyHeatmap,
  fetchSLA,
  fetchBotConversion,
  fetchTopProducts,
  fetchUsers,
  createUser,
  updateUser,
  updateRestaurantConfig,
} from '../services/adminApi';
import { fetchAdminCatalog, updateCatalogItem, createCatalogItem, setBranchOverride } from '../services/catalogApi';
import { fetchLive } from '../services/adminApi';
import { fetchBranches } from '../services/branchesApi';
import { useRestaurant } from '../contexts/RestaurantContext';
import '../styles/admin.css';

const VALID_ROLES = ['cocina', 'mostrador', 'soporte', 'admin'];

const PERIODS = [
  { value: 'today', label: 'Hoy' },
  { value: 'week',  label: 'Últimos 7 días' },
  { value: 'month', label: 'Últimos 30 días' },
];

const CHANNEL_COLORS = { WHATSAPP: '#25d366', FRONTDESK: '#f97316' };
const PIE_COLORS = ['#25d366', '#f97316', '#60a5fa', '#a78bfa'];

const STATE_LABELS = {
  ASK_BRANCH:     'Selec. sucursal',
  ASK_SIZE:       'Selec. tamaño',
  ASK_SPECIALTY:  'Selec. especialidad',
  ASK_NOTES:      'Notas',
  ASK_MORE_PIZZA: 'Agregar pizza',
  CONFIRM_ORDER:  'Confirmar pedido',
  ASK_NAME:       'Dar nombre',
};

// ── Helpers ──────────────────────────────────

const fmt = (n) =>
  n == null ? '—' : Number(n).toLocaleString('es-MX', { minimumFractionDigits: 0 });

const fmtMoney = (n) =>
  n == null
    ? '—'
    : `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtMin = (n) =>
  n == null ? <span className="na">Sin datos</span> : `${n} min`;

// ── Sub-componentes ───────────────────────────

function KPICards({ data }) {
  if (!data) return <div className="admin-loading">Cargando…</div>;
  return (
    <div className="kpi-row">
      <div className="kpi-card">
        <span className="kpi-label">Órdenes totales</span>
        <span className="kpi-value">{fmt(data.total_orders)}</span>
      </div>
      <div className="kpi-card">
        <span className="kpi-label">Ingresos</span>
        <span className="kpi-value">{fmtMoney(data.total_revenue)}</span>
      </div>
      <div className="kpi-card">
        <span className="kpi-label">Ticket promedio</span>
        <span className="kpi-value">{fmtMoney(data.avg_ticket)}</span>
      </div>
      <div className="kpi-card">
        <span className="kpi-label">% Canceladas</span>
        <span className="kpi-value" style={{ color: data.cancelled_pct > 10 ? '#ef4444' : '#f97316' }}>
          {data.cancelled_pct}%
        </span>
      </div>
    </div>
  );
}

function OrdersByBranchChart({ data }) {
  if (!data) return <div className="admin-loading">Cargando…</div>;
  if (!data.length) return <div className="admin-empty">Sin datos en este período</div>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#2d2d2f" />
        <XAxis dataKey="branch_name" tick={{ fill: '#9ca3af', fontSize: 12 }} />
        <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
        <Tooltip
          contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }}
          labelStyle={{ color: '#f9fafb' }}
          itemStyle={{ color: '#e5e7eb' }}
          formatter={(v, name) => [fmt(v), name === 'order_count' ? 'Órdenes' : 'Ingresos']}
        />
        <Bar dataKey="order_count" fill="#f97316" radius={[4, 4, 0, 0]} name="Órdenes" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function OrdersByChannelChart({ data }) {
  if (!data) return <div className="admin-loading">Cargando…</div>;
  if (!data.length) return <div className="admin-empty">Sin datos en este período</div>;

  const pieData = data.map((d) => ({
    name: d.channel === 'WHATSAPP' ? 'WhatsApp' : 'Mostrador',
    value: d.order_count,
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={pieData}
          cx="50%"
          cy="50%"
          innerRadius={55}
          outerRadius={85}
          paddingAngle={3}
          dataKey="value"
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {pieData.map((_, i) => (
            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }}
          itemStyle={{ color: '#e5e7eb' }}
        />
        <Legend wrapperStyle={{ fontSize: 12, color: '#9ca3af' }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function HourlyHeatmap({ data }) {
  if (!data) return <div className="admin-loading">Cargando…</div>;
  const max = Math.max(...data.map((d) => d.order_count), 1);
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#2d2d2f" vertical={false} />
        <XAxis
          dataKey="hour"
          tickFormatter={(h) => `${h}h`}
          tick={{ fill: '#9ca3af', fontSize: 10 }}
          interval={2}
        />
        <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
        <Tooltip
          contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }}
          labelFormatter={(h) => `${h}:00 hrs`}
          formatter={(v) => [v, 'Órdenes']}
          itemStyle={{ color: '#e5e7eb' }}
        />
        <Bar dataKey="order_count" radius={[2, 2, 0, 0]} name="Órdenes">
          {data.map((d, i) => (
            <Cell
              key={i}
              fill={
                d.order_count === max
                  ? '#ef4444'
                  : d.order_count >= max * 0.6
                  ? '#f97316'
                  : '#374151'
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function SLAPanel({ data }) {
  if (!data) return <div className="admin-loading">Cargando…</div>;
  return (
    <div className="sla-grid">
      <div className="sla-item">
        <span className="sla-label">PENDING → PREPARING</span>
        <span className={`sla-value ${data.avg_pending_to_preparing_min == null ? 'na' : ''}`}>
          {fmtMin(data.avg_pending_to_preparing_min)}
        </span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>
          {data.sample_size_pending_preparing} muestras
        </span>
      </div>
      <div className="sla-item">
        <span className="sla-label">PREPARING → READY</span>
        <span className={`sla-value ${data.avg_preparing_to_ready_min == null ? 'na' : ''}`}>
          {fmtMin(data.avg_preparing_to_ready_min)}
        </span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>
          {data.sample_size_preparing_ready} muestras
        </span>
      </div>
      <div className="sla-item">
        <span className="sla-label">Órdenes en rojo (&gt;15 min)</span>
        <span className={`sla-value ${data.orders_in_red_count > 0 ? 'red' : ''}`}>
          {data.orders_in_red_count}
        </span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>activas ahora</span>
      </div>
    </div>
  );
}

function BotConversionPanel({ data }) {
  if (!data) return <div className="admin-loading">Cargando…</div>;

  const stateEntries = Object.entries(data.abandonment_by_state || {}).sort(
    ([, a], [, b]) => b - a
  );
  const maxCount = stateEntries.length ? stateEntries[0][1] : 1;

  return (
    <div className="bot-conv-row">
      <div className="bot-conv-stat">
        <span className="conv-label">Tasa de conversión</span>
        <span className="conv-value">{data.conversion_pct}%</span>
        <span style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
          {data.completed_orders} pedidos / {data.total_sessions} sesiones
        </span>
      </div>

      {stateEntries.length > 0 && (
        <div className="abandonment-list">
          <h3>Abandono por estado</h3>
          {stateEntries.map(([state, count]) => (
            <div className="abandonment-bar-row" key={state}>
              <span className="state-name">{STATE_LABELS[state] || state}</span>
              <div className="abandonment-bar-bg">
                <div
                  className="abandonment-bar-fill"
                  style={{ width: `${(count / maxCount) * 100}%` }}
                />
              </div>
              <span className="state-count">{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TopProductsTable({ data }) {
  if (!data) return <div className="admin-loading">Cargando…</div>;
  if (!data.length) return <div className="admin-empty">Sin datos en este período</div>;
  return (
    <table className="top-products-table">
      <thead>
        <tr>
          <th className="rank">#</th>
          <th>Producto</th>
          <th>Cantidad</th>
          <th>Revenue</th>
        </tr>
      </thead>
      <tbody>
        {data.map((p, i) => (
          <tr key={i}>
            <td className="rank">{i + 1}</td>
            <td>{p.product_name}</td>
            <td>{fmt(p.qty_sold)}</td>
            <td className="revenue">{fmtMoney(p.revenue)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Vista en vivo ─────────────────────────────

const STATUS_LABEL = { PENDING: 'Pendiente', PREPARING: 'En preparación', READY: 'Listo' };
const STATUS_COLOR = { PENDING: '#f59e0b', PREPARING: '#3b82f6', READY: '#22c55e' };

function elapsed(isoDate) {
  if (!isoDate) return '—';
  const s = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(isoDate) ? isoDate : isoDate + 'Z';
  const mins = Math.floor((Date.now() - new Date(s)) / 60000);
  return mins < 1 ? '< 1 min' : `${mins} min`;
}

function LiveTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    fetchLive()
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  if (error) return <div style={{ padding: 40, color: '#f87171' }}>Error: {error}</div>;
  if (!data) return <div style={{ padding: 40, color: '#888' }}>Cargando...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <span style={{ color: '#9ca3af', fontSize: 13 }}>Actualización automática cada 15 s</span>
        <button onClick={load} style={{ background: '#1f2937', color: '#9ca3af', border: '1px solid #374151', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>
          ↻ Actualizar
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
        {data.map((branch) => (
          <div key={branch.branch_id} className="admin-panel" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Cabecera sucursal */}
            <div style={{ background: '#1e3a5f', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 'bold', fontSize: 16, color: '#e5e7eb' }}>🏪 {branch.branch_name}</span>
              <span style={{ fontSize: 13, color: '#93c5fd' }}>
                {branch.counts.PENDING + branch.counts.PREPARING + branch.counts.READY} activos
              </span>
            </div>

            {/* Badges de conteo */}
            <div style={{ display: 'flex', gap: 10, padding: '12px 18px', borderBottom: '1px solid #1f2937' }}>
              {Object.entries(branch.counts).map(([status, count]) => (
                <span key={status} style={{
                  background: STATUS_COLOR[status] + '22',
                  color: STATUS_COLOR[status],
                  border: `1px solid ${STATUS_COLOR[status]}44`,
                  borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 'bold',
                }}>
                  {STATUS_LABEL[status]}: {count}
                </span>
              ))}
            </div>

            {/* Lista de órdenes */}
            <div style={{ padding: '10px 18px 16px' }}>
              {branch.orders.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>Sin órdenes activas</p>
              ) : (
                branch.orders.map((order) => (
                  <div key={order.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 0', borderBottom: '1px solid #1f2937', fontSize: 13,
                  }}>
                    <div>
                      <span style={{ color: '#e5e7eb', fontWeight: 500 }}>#{order.id} {order.customer_name}</span>
                      <span style={{
                        marginLeft: 8, background: STATUS_COLOR[order.status] + '22',
                        color: STATUS_COLOR[order.status], borderRadius: 10, padding: '1px 7px', fontSize: 11,
                      }}>
                        {STATUS_LABEL[order.status]}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right', color: '#9ca3af' }}>
                      <div style={{ color: '#e5e7eb' }}>${order.total_amount.toFixed(2)}</div>
                      <div style={{ fontSize: 11 }}>{elapsed(order.preparing_at || order.created_at)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Catálogo ──────────────────────────────────

const EMPTY_FORMS = {
  'pizza-sizes': { name: '', price: '', max_specialties: 2, active: true },
  drinks:        { name: '', size: '', price: '', active: true },
  packages:      { name: '', price: '', active: true },
  specialties:   { name: '', description: '', active: true },
};

const PRODUCT_TYPE_MAP = {
  'pizza-sizes': 'pizza_size',
  'drinks': 'drink',
  'packages': 'package',
  'specialties': 'specialty',
};

function CatalogTab() {
  const [catalog, setCatalog] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null); // null = global
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBranches().then(setBranches).catch(() => {});
  }, []);

  const load = useCallback(() => {
    fetchAdminCatalog(selectedBranch).then(setCatalog).catch(() => {});
  }, [selectedBranch]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (type, item) => { setForm({ ...item }); setModal({ type, item }); };
  const openCreate = (type) => { setForm({ ...EMPTY_FORMS[type] }); setModal({ type, item: null }); };

  const save = async () => {
    setSaving(true);
    try {
      if (modal.item) {
        await updateCatalogItem(modal.type, modal.item.id, form);
      } else {
        await createCatalogItem(modal.type, form);
      }
      setModal(null);
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (type, item) => {
    if (selectedBranch) {
      await setBranchOverride(PRODUCT_TYPE_MAP[type], item.id, selectedBranch, !item.active);
    } else {
      await updateCatalogItem(type, item.id, { ...item, active: !item.active });
    }
    load();
  };

  if (!catalog) return <div style={{ padding: 40, color: '#888' }}>Cargando catálogo...</div>;

  const sections = [
    { type: 'pizza-sizes', label: '🍕 Tamaños de Pizza', items: catalog.pizza_sizes,
      cols: ['Nombre', 'Precio', 'Máx. esp.', 'Activo'], row: (i) => [i.name, `$${i.price}`, i.max_specialties] },
    { type: 'drinks', label: '🥤 Bebidas', items: catalog.drinks,
      cols: ['Nombre', 'Tamaño', 'Precio', 'Activo'], row: (i) => [i.name, i.size, `$${i.price}`] },
    { type: 'packages', label: '📦 Paquetes / Combos', items: catalog.packages,
      cols: ['Nombre', 'Precio', 'Activo'], row: (i) => [i.name, `$${i.price}`] },
    { type: 'specialties', label: '🧀 Especialidades', items: catalog.specialties,
      cols: ['Nombre', 'Descripción', 'Activo'], row: (i) => [i.name, i.description || '—'] },
  ];

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #374151', background: '#1f2937', color: '#e5e7eb', marginBottom: 10 };
  const labelStyle = { display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 };

  return (
    <div style={{ padding: '0 0 40px' }}>

      {/* Selector de sucursal */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <span style={{ color: '#9ca3af', fontSize: 13 }}>Ver como:</span>
        {[{ id: null, name: 'Global (todas las sucursales)' }, ...branches].map(b => (
          <button
            key={b.id ?? 'global'}
            onClick={() => setSelectedBranch(b.id)}
            style={{
              padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
              background: selectedBranch === b.id ? '#1e3a5f' : '#1f2937',
              color: selectedBranch === b.id ? '#93c5fd' : '#9ca3af',
              fontWeight: selectedBranch === b.id ? 'bold' : 'normal',
            }}
          >
            {b.name}
          </button>
        ))}
        {selectedBranch && (
          <span style={{ fontSize: 12, color: '#f59e0b', background: '#451a03', borderRadius: 6, padding: '4px 10px' }}>
            El toggle 🟢/🔴 modifica solo esta sucursal
          </span>
        )}
      </div>

      {sections.map(({ type, label, items, cols, row }) => (
        <div key={type} className="admin-panel" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ margin: 0 }}>{label}</h2>
            {!selectedBranch && (
              <button
                onClick={() => openCreate(type)}
                style={{ background: '#1e3a5f', color: '#93c5fd', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}
              >
                + Agregar
              </button>
            )}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #374151' }}>
                {cols.map(c => <th key={c} style={{ textAlign: 'left', padding: '6px 8px', color: '#9ca3af', fontWeight: 500 }}>{c}</th>)}
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#9ca3af' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid #1f2937', opacity: item.active ? 1 : 0.45 }}>
                  {row(item).map((val, i) => (
                    <td key={i} style={{ padding: '8px 8px', color: '#e5e7eb' }}>{val}</td>
                  ))}
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                    <span
                      onClick={() => toggleActive(type, item)}
                      title={item.active ? 'Desactivar' : 'Activar'}
                      style={{ cursor: 'pointer', marginRight: 10, fontSize: 16 }}
                    >
                      {item.active ? '🟢' : '🔴'}
                    </span>
                    {!selectedBranch && (
                      <button
                        onClick={() => openEdit(type, item)}
                        style={{ background: '#374151', color: '#e5e7eb', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}
                      >
                        Editar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#111827', borderRadius: 12, padding: 28, width: 360, maxWidth: '95vw' }}>
            <h3 style={{ margin: '0 0 20px', color: '#e5e7eb' }}>
              {modal.item ? 'Editar' : 'Agregar'} {modal.type === 'pizza-sizes' ? 'pizza' : modal.type === 'specialties' ? 'especialidad' : modal.type.slice(0, -1)}
            </h3>

            <label style={labelStyle}>Nombre</label>
            <input style={inputStyle} value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} />

            {modal.type === 'drinks' && (
              <>
                <label style={labelStyle}>Tamaño (ej: 600ml)</label>
                <input style={inputStyle} value={form.size || ''} onChange={e => setForm({ ...form, size: e.target.value })} />
              </>
            )}

            {modal.type === 'specialties' ? (
              <>
                <label style={labelStyle}>Descripción</label>
                <input style={inputStyle} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
              </>
            ) : (
              <>
                <label style={labelStyle}>Precio</label>
                <input style={inputStyle} type="number" value={form.price || ''} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) })} />
              </>
            )}

            {modal.type === 'pizza-sizes' && (
              <>
                <label style={labelStyle}>Máx. especialidades</label>
                <input style={inputStyle} type="number" value={form.max_specialties || ''} onChange={e => setForm({ ...form, max_specialties: parseInt(e.target.value) })} />
              </>
            )}

            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4 }}>
              <input type="checkbox" checked={form.active ?? true} onChange={e => setForm({ ...form, active: e.target.checked })} />
              <span style={{ color: '#e5e7eb' }}>Activo</span>
            </label>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => setModal(null)} style={{ flex: 1, padding: 10, background: '#374151', color: '#e5e7eb', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={save} disabled={saving} style={{ flex: 1, padding: 10, background: '#1e3a5f', color: '#93c5fd', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Usuarios del tenant ───────────────────────

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', role: 'mostrador' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    fetchUsers().then(setUsers).catch((e) => setError(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRoleChange = async (user, role) => {
    try {
      await updateUser(user.id, { role });
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await createUser(form);
      setForm({ username: '', password: '', role: 'mostrador' });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #374151', background: '#1f2937', color: '#e5e7eb', marginBottom: 10 };
  const labelStyle = { display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 };

  if (!users) return <div style={{ padding: 40, color: '#888' }}>Cargando usuarios...</div>;

  return (
    <div style={{ padding: '0 0 40px' }}>
      {error && (
        <div style={{ background: '#450a0a', color: '#fca5a5', padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div className="admin-panel" style={{ marginBottom: 24 }}>
        <h2 style={{ marginTop: 0 }}>👥 Usuarios del restaurante</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #374151' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9ca3af', fontWeight: 500 }}>Usuario</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9ca3af', fontWeight: 500 }}>Rol</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={{ borderBottom: '1px solid #1f2937' }}>
                <td style={{ padding: '8px 8px', color: '#e5e7eb' }}>{u.username}</td>
                <td style={{ padding: '8px 8px' }}>
                  <select
                    value={u.role}
                    onChange={(e) => handleRoleChange(u, e.target.value)}
                    style={{ background: '#1f2937', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 6, padding: '4px 8px' }}
                  >
                    {VALID_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-panel" style={{ maxWidth: 360 }}>
        <h2 style={{ marginTop: 0 }}>+ Crear usuario</h2>
        <form onSubmit={handleCreate}>
          <label style={labelStyle}>Usuario</label>
          <input style={inputStyle} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />

          <label style={labelStyle}>Contraseña</label>
          <input style={inputStyle} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />

          <label style={labelStyle}>Rol</label>
          <select style={inputStyle} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {VALID_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>

          <button type="submit" disabled={creating} style={{ width: '100%', padding: 10, background: '#1e3a5f', color: '#93c5fd', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>
            {creating ? 'Creando...' : 'Crear usuario'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Configuración del restaurante ─────────────

function RestaurantConfigTab() {
  const { restaurant, reload } = useRestaurant();
  const [form, setForm] = useState({ logo_url: '', primary_color: '#ef4444', timezone: 'America/Mexico_City' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (restaurant) {
      setForm({
        logo_url: restaurant.logo_url || '',
        primary_color: restaurant.primary_color || '#ef4444',
        timezone: restaurant.timezone || 'America/Mexico_City',
      });
    }
  }, [restaurant]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateRestaurantConfig(form);
      await reload();
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #374151', background: '#1f2937', color: '#e5e7eb', marginBottom: 10 };
  const labelStyle = { display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 };

  return (
    <div className="admin-panel" style={{ maxWidth: 420 }}>
      <h2 style={{ marginTop: 0 }}>⚙️ Configuración del restaurante</h2>
      <form onSubmit={handleSave}>
        <label style={labelStyle}>Logo (URL)</label>
        <input
          style={inputStyle}
          value={form.logo_url}
          onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
          placeholder="https://..."
        />

        <label style={labelStyle}>Color principal</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <input
            type="color"
            value={form.primary_color}
            onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            style={{ width: 44, height: 32, padding: 0, border: '1px solid #374151', borderRadius: 6, background: 'none', cursor: 'pointer' }}
          />
          <span style={{ color: '#9ca3af', fontSize: 13 }}>{form.primary_color}</span>
        </div>

        <label style={labelStyle}>Zona horaria</label>
        <input
          style={inputStyle}
          value={form.timezone}
          onChange={(e) => setForm({ ...form, timezone: e.target.value })}
        />

        {error && <div style={{ color: '#fca5a5', fontSize: 13, marginBottom: 10 }}>{error}</div>}
        {saved && <div style={{ color: '#4ade80', fontSize: 13, marginBottom: 10 }}>Guardado ✓</div>}

        <button type="submit" disabled={saving} style={{ width: '100%', padding: 10, background: '#1e3a5f', color: '#93c5fd', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}>
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </form>
    </div>
  );
}

// ── Componente principal ──────────────────────

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('metrics');
  const [period, setPeriod] = useState('week');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);

  const [summary, setSummary]       = useState(null);
  const [byBranch, setByBranch]     = useState(null);
  const [byChannel, setByChannel]   = useState(null);
  const [heatmap, setHeatmap]       = useState(null);
  const [sla, setSla]               = useState(null);
  const [botConv, setBotConv]       = useState(null);
  const [topProds, setTopProds]     = useState(null);
  const [error, setError]           = useState(null);

  useEffect(() => {
    fetchBranches().then(setBranches).catch(() => {});
  }, []);

  const loadAll = useCallback(() => {
    const bid = branchId ? Number(branchId) : null;
    setError(null);

    // Reset para mostrar loading mientras se recargan
    setSummary(null); setByBranch(null); setByChannel(null);
    setHeatmap(null); setSla(null); setBotConv(null); setTopProds(null);

    Promise.all([
      fetchSummary(period, bid),
      fetchOrdersByBranch(period),
      fetchOrdersByChannel(period, bid),
      fetchHourlyHeatmap(period, bid),
      fetchSLA(period, bid),
      fetchBotConversion(period),
      fetchTopProducts(period, bid),
    ])
      .then(([sum, branch, channel, heat, slaData, bot, prods]) => {
        setSummary(sum);
        setByBranch(branch);
        setByChannel(channel);
        setHeatmap(heat);
        setSla(slaData);
        setBotConv(bot);
        setTopProds(prods);
      })
      .catch((e) => setError(e.message));
  }, [period, branchId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return (
    <div className="admin-dashboard">
      {/* Cabecera */}
      <div className="admin-header">
        <h1>Dashboard Admin</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { key: 'metrics', label: '📊 Métricas' },
            { key: 'live', label: '🏪 En vivo' },
            { key: 'catalog', label: '🍕 Catálogo' },
            { key: 'users', label: '👥 Usuarios' },
            { key: 'config', label: '⚙️ Configuración' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
                background: activeTab === key ? '#1e3a5f' : '#1f2937',
                color: activeTab === key ? '#93c5fd' : '#9ca3af',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {activeTab === 'metrics' && (
          <div className="admin-filters">
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Todas las sucursales</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <button
              onClick={loadAll}
              style={{
                background: '#1f2937', color: '#e5e7eb', border: '1px solid #374151',
                borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13,
              }}
            >
              Actualizar
            </button>
          </div>
        )}
      </div>

      {activeTab === 'catalog' && <CatalogTab />}
      {activeTab === 'live' && <LiveTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'config' && <RestaurantConfigTab />}

      {activeTab === 'metrics' && <>
      {error && (
        <div style={{ background: '#450a0a', color: '#fca5a5', padding: '12px 16px',
          borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
          Error al cargar métricas: {error}
        </div>
      )}

      <div className="admin-grid">

        {/* KPIs */}
        <KPICards data={summary} />

        {/* Órdenes por sucursal + por canal */}
        <div className="admin-two-col">
          <div className="admin-panel">
            <h2>Órdenes por sucursal</h2>
            <OrdersByBranchChart data={byBranch} />
          </div>
          <div className="admin-panel">
            <h2>Canal de origen</h2>
            <OrdersByChannelChart data={byChannel} />
          </div>
        </div>

        {/* Horas pico */}
        <div className="admin-panel">
          <h2>Horas pico de demanda (hora local México)</h2>
          <HourlyHeatmap data={heatmap} />
        </div>

        {/* SLA + Bot conversion */}
        <div className="admin-two-col">
          <div className="admin-panel">
            <h2>Tiempos de preparación (SLA)</h2>
            <SLAPanel data={sla} />
          </div>
          <div className="admin-panel">
            <h2>Conversión del bot WhatsApp</h2>
            <BotConversionPanel data={botConv} />
          </div>
        </div>

        {/* Top productos */}
        <div className="admin-panel">
          <h2>Top productos más vendidos</h2>
          <TopProductsTable data={topProds} />
        </div>

      </div>
      </>}
    </div>
  );
}
